import { Get, Put } from 'config/apiServiceConfig'
import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import { OrderItemType } from '../redux/AllOrder'
import { CommonNameValueObjectType, OrderV2ResponseType } from '../redux/AllOrder_V2'
import { getAvailableStaff } from './staffAvailability'

export type OrderTypesItemType = 'INSTANT' | 'GRAB_MART' | 'SUPER' | 'KITCHEN' | 'VENDING'

export type OrderType = OrderTypesItemType[]

export type AllOrderResponseType = {
  content: OrderItemType[]
  empty: boolean
  first: boolean
  last: boolean
  number: number
  numberOfElements: number
  pageable: {
    sort: {
      sorted: boolean
      unsorted: boolean
      empty: boolean
    }
    offset: number
    pageSize: number
    pageNumber: number
    paged: boolean
    unpaged: boolean
  }
  size: number
  sort: {
    sorted: boolean
    unsorted: boolean
    empty: boolean
  }
}

export type OrderLocationType = {
  location_id: number
  location_name: string
  location_type: string
}

export type OrderLocationResponseType = {
  data: OrderLocationType[]
  error: {
    status: boolean
    message: string
    code: number
  }
  pagination: null
}

type FilterOrderResponseType = {
  order_types: CommonNameValueObjectType[]
  order_status: CommonNameValueObjectType[]
  slas: CommonNameValueObjectType[]
  locations: CommonNameValueObjectType[]
}

export type PoolingResponseType = {
  is_new_customer?: boolean
  pool_id?: number
  formatted_pool_number: string
  order_created_at: number
  order_customer_address: string
  order_customer_name: string
  order_id: number
  order_location_quadrant: number
  order_payment_verified_at: number
  order_service_level_name: string
  order_status: string
  pool_number: string
}

type CancelOrderPayloadType = {
  order_id: number
  customer_service_email: string
  customer_service_password: string
  note: string
  out_of_stock?: boolean
}
export type OrderReturnableItemType = {
  returnable_items: {
    product_id: string
    product_name: string
    sku: string
    max_quantity: number
    image_url: string
    qty?: number
  }[]
}

export type AstroApprovalPinAuthenticationType = {
  astro_approval_pin_authentication: {
    phone_number: string
    pin: string
  }
}

export type OrderReturnPayloadType = AstroApprovalPinAuthenticationType & {
  id: number
  delivery_proof_urls: string[]
  note: string
  return_items: {
    product_id: string
    qty: number
  }[]
}

export type CancelOrderV2PayloadType = {
  order_id: number
  note: string
  astro_approval_pin_authentication: {
    phone_number: string
    pin: string
    location_id: number
  }
  is_sudo: boolean
}

export type GetDriverPayloadType = {
  role: string
  locationId: number
  query: Record<string, unknown>
}

export type ReturnableItemsWithQtyType = (OrderReturnableItemType['returnable_items'][number] & {
  qty: number
})[]

export const getOrderTypes = () => Get<{ data: OrderType }>({ url: '/api/order/types' })

export const getAllOrder = (query: string) =>
  Get<AllOrderResponseType>({ url: `/api/order${query}` })

export const getOrderLocations = () =>
  Get<OrderLocationResponseType>({ url: `/api/locations/order` })

export const getAllOrderV2API = (query: string) =>
  axiosInstanceApiGateway.Get<OrderV2ResponseType>({ url: `/oms/internal/v2/orders${query}` })
export const getAllFiltersV2 = () =>
  axiosInstanceApiGateway.Get<FilterOrderResponseType>({
    url: '/oms/internal/v2/orders/filters',
  })
export const cancelOrderAPI = (payload: CancelOrderPayloadType) =>
  Put<{ order_invoice: number }>({ url: `/api/order/${payload.order_id}/cancel`, data: payload })

export const sudoCancelOrderAPI = (payload: CancelOrderPayloadType) =>
  Put<{ order_invoice: number }>({
    url: `/api/order/${payload.order_id}/super-cancel`,
    data: payload,
  })

export const cancelReasonAPI = () =>
  axiosInstanceApiGateway.Get<{ reasons: { value: string; key: string }[] }>({
    url: '/oms/internal/v1/action-reasons?action=CANCEL_ORDER',
  })

export const cancelReasonSudoAPI = () =>
  Get<{ data: { description: string; key: string }[] }>({
    url: '/api/order/cancel-complete-reason',
  })
export const getReturnableItems = (orderID: number) =>
  axiosInstanceApiGateway.Get<OrderReturnableItemType>({
    url: `/fulfillment/internal/v1/driver/orders/${orderID}/returnable-items`,
  })

export const submitReturnOrder = (payload: OrderReturnPayloadType) =>
  axiosInstanceApiGateway.Post<OrderReturnableItemType>({
    url: `/oms/internal/v2/orders/${payload.id}/complete`,
    data: payload,
  })

export const submitCancelV2 = (payload: CancelOrderV2PayloadType) =>
  axiosInstanceApiGateway.Post({
    url: `/oms/internal/v2/orders/${payload.order_id}/cancellation`,
    data: payload,
  })

export const submitCompleteV2 = (payload: OrderReturnPayloadType) =>
  axiosInstanceApiGateway.Post({
    url: `/oms/internal/v2/orders/${payload.id}/complete`,
    data: payload,
  })

export const getDriverByRole = (payload: GetDriverPayloadType) => {
  const { role, locationId } = payload
  return getAvailableStaff(locationId, role)
}

export const getFeatureFlagByLocationID = (payload: {
  locationId: number
  query: Record<string, unknown>
}) => {
  const { locationId, query } = payload
  return Get<{ data: { features: Record<string, boolean> } }>({
    url: `/api/feature-flag/${locationId}/1`,
    urlParams: query,
  })
}

export const getSupervisors = (locationId: string) =>
  Get<{
    data: {
      selectedLocation: {
        supervisors: Record<string, unknown>[]
        location_pic_3pl_id: string
      }
    }
  }>({
    url: `/api/location/${locationId}`,
  })
export const postContactCustomer = (order_id: number) =>
  axiosInstanceApiGateway.Post({
    url: `/oms/internal/v2/orders/${order_id}/contact-customer`,
  })

export const continueOrder = (order_id: number) =>
  Put({
    url: `/api/order/${order_id}/approve-partial-fulfill`,
  })
